/* A searchable single-select — the housing lists run to 23 options on Sony alone, and a native
 * <select> gives no way to type at them.
 *
 * Shared by dvl-gear-finder.js and dvl-gear-picker.js (both load this first), so the two surfaces get
 * the same control rather than two near-copies. Exposed as window.DvlGearCombo.
 *
 * Follows the ARIA combobox pattern: a text input with role="combobox" owning a role="listbox", with
 * aria-activedescendant tracking the highlighted option so screen readers announce it without the
 * focus ever leaving the input. Keyboard: Down/Up to move (Down opens), Enter to take the highlight,
 * Escape to close and restore the current value, Home/End to jump, Tab to leave.
 *
 * It is dressed as a dropdown on purpose — a bare text box does not tell a shopper there is a list
 * behind it. The chevron follows the theme's own convention for one (Dawn positions .icon-caret
 * absolutely at the right and rotates it 180 degrees when open) but is drawn inline rather than
 * borrowed from assets/icon-caret.svg, so this file stays portable across the four themes.
 */
(() => {
  let seq = 0;

  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

  class DvlGearCombo {
    /** @param {{items?: Array<{value:string,label:string}>, placeholder?: string,
     *           emptyText?: string, onChange?: (value:string|null)=>void}} opt */
    constructor(opt = {}) {
      this.items = opt.items || [];
      this.placeholder = opt.placeholder || "";
      this.emptyText = opt.emptyText || "Nothing matches";
      this.onChange = opt.onChange || (() => {});
      this.value = null;
      this.open = false;
      this.active = -1;
      this.id = `dvl-g-combo-${++seq}`;
      this.build();
    }

    build() {
      const wrap = document.createElement("div");
      wrap.className = "dvl-g__combo";

      const input = document.createElement("input");
      input.type = "text";
      input.className = "dvl-g__search dvl-g__combo-input";
      input.setAttribute("role", "combobox");
      input.setAttribute("aria-expanded", "false");
      input.setAttribute("aria-controls", `${this.id}-list`);
      input.setAttribute("aria-autocomplete", "list");
      input.setAttribute("autocomplete", "off");
      input.placeholder = this.placeholder;

      const list = document.createElement("ul");
      list.className = "dvl-g__combo-list";
      list.id = `${this.id}-list`;
      list.setAttribute("role", "listbox");
      list.hidden = true;

      // the affordance: a chevron that says "there is a list here", rotating when it opens
      const caret = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      caret.setAttribute("class", "dvl-g__combo-caret");
      caret.setAttribute("viewBox", "0 0 12 8");
      caret.setAttribute("aria-hidden", "true");
      caret.setAttribute("focusable", "false");
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", "M1 1.5 6 6.5l5-5");
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "1.6");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      caret.append(path);

      wrap.append(input, caret, list);
      this.el = wrap;
      this.input = input;
      this.list = list;

      input.addEventListener("focus", () => this.show(""));
      // clicking an already-focused field toggles the list, as a native select would. The first click
      // is left to `focus` above; without the closed branch a click after Escape would do nothing.
      input.addEventListener("mousedown", () => {
        if (document.activeElement !== input) return;
        if (this.open) {
          this.hide();
          input.value = this.labelOf(this.value);
        } else {
          this.show("");
        }
      });
      input.addEventListener("input", () => this.show(input.value));
      /* The keyboard arrives a beat AFTER focus and leaves again on blur, and each is a visualViewport
         resize, not a window one — this is the only event that reports it. Scroll counts too: iOS
         scrolls the visual viewport to keep the caret in sight. */
      const replace = () => this.place();
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", replace);
        window.visualViewport.addEventListener("scroll", replace);
      }
      window.addEventListener("resize", replace);
      input.addEventListener("keydown", (e) => this.key(e));
      // mousedown, not click: the input's blur would close the list before a click landed
      list.addEventListener("mousedown", (e) => {
        const li = e.target.closest("li[data-value]");
        if (!li) return;
        e.preventDefault();
        this.pick(li.dataset.value);
      });
      input.addEventListener("blur", () => {
        // let a list mousedown win the race, then settle the input back to the current value
        setTimeout(() => {
          if (this.el.contains(document.activeElement)) return;
          this.hide();
          this.input.value = this.labelOf(this.value);
        }, 120);
      });
    }

    labelOf(value) {
      const it = this.items.find((i) => i.value === value);
      return it ? it.label : "";
    }

    setItems(items, { keepValue = false } = {}) {
      this.items = items || [];
      if (!keepValue || !this.items.some((i) => i.value === this.value)) this.value = null;
      this.input.value = this.labelOf(this.value);
      if (this.open) this.show(this.input.value === this.labelOf(this.value) ? "" : this.input.value);
      return this;
    }

    setValue(value, { silent = true } = {}) {
      this.value = this.items.some((i) => i.value === value) ? value : null;
      this.input.value = this.labelOf(this.value);
      if (!silent) this.onChange(this.value);
      return this;
    }

    set disabled(v) {
      this.input.disabled = !!v;
      if (v) this.hide();
    }

    get disabled() {
      return this.input.disabled;
    }

    /** The options a query admits. An empty query shows everything, so opening the control still
     *  behaves like a dropdown rather than demanding that the shopper type. */
    filtered(q) {
      const n = norm(q);
      if (!n) return this.items;
      return this.items.filter((i) => norm(i.label).includes(n));
    }

    show(q) {
      const rows = this.filtered(q);
      this.list.textContent = "";
      if (!rows.length) {
        const li = document.createElement("li");
        li.className = "dvl-g__combo-empty";
        li.textContent = this.emptyText;
        this.list.append(li);
      }
      rows.forEach((it, i) => {
        const li = document.createElement("li");
        li.className = "dvl-g__combo-opt";
        li.id = `${this.id}-o${i}`;
        li.dataset.value = it.value;
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", String(it.value === this.value));
        li.textContent = it.label;
        this.list.append(li);
      });
      this.rows = rows;
      this.active = rows.findIndex((i) => i.value === this.value);
      this.list.hidden = false;
      this.open = true;
      this.input.setAttribute("aria-expanded", "true");
      this.highlight(this.active);
      this.place();
    }

    /* FIT THE LIST INTO WHAT THE KEYBOARD LEAVES.
     *
     * The list opens because the field took focus, and taking focus is exactly what raises the
     * on-screen keyboard. The keyboard does not resize the layout viewport — it shrinks the VISUAL one
     * — so a list sized in `em` and pinned below the field keeps its full height and spends most of it
     * behind the keyboard. The options are there; they cannot be seen or reached.
     *
     * So measure both sides of the field against `visualViewport` and open into whichever is roomier.
     * With the keyboard up that is almost always ABOVE the field. The height is then capped to the room
     * that actually exists, and the list scrolls inside it instead of running off the screen.
     */
    place() {
      if (!this.open) return;
      const vv = window.visualViewport;
      const top = vv ? vv.offsetTop : 0;
      const height = vv ? vv.height : window.innerHeight;
      const r = this.input.getBoundingClientRect();
      const gap = 10;
      const below = top + height - r.bottom - gap;
      const above = r.top - top - gap;
      // only flip when above is genuinely roomier; a near-tie keeps the familiar downward list
      const up = above > below + 40 && above > 120;
      this.list.classList.toggle("dvl-g__combo-list--up", up);
      const cap = 17 * (parseFloat(getComputedStyle(this.list).fontSize) || 16);
      const room = Math.max(96, Math.min(cap, Math.floor(up ? above : below)));
      this.list.style.maxHeight = `${room}px`;
    }

    hide() {
      this.list.hidden = true;
      this.list.classList.remove("dvl-g__combo-list--up");
      this.list.style.maxHeight = "";
      this.open = false;
      this.active = -1;
      this.input.setAttribute("aria-expanded", "false");
      this.input.removeAttribute("aria-activedescendant");
    }

    highlight(i) {
      const opts = [...this.list.querySelectorAll("li[data-value]")];
      opts.forEach((o) => o.classList.remove("is-active"));
      this.active = i;
      const cur = opts[i];
      if (!cur) {
        this.input.removeAttribute("aria-activedescendant");
        return;
      }
      cur.classList.add("is-active");
      this.input.setAttribute("aria-activedescendant", cur.id);
      if (cur.scrollIntoView) cur.scrollIntoView({ block: "nearest" });
    }

    pick(value) {
      this.value = value;
      this.input.value = this.labelOf(value);
      this.hide();
      this.onChange(value || null);
    }

    key(e) {
      const n = (this.rows || []).length;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!this.open) return this.show("");
          this.highlight(this.active + 1 >= n ? 0 : this.active + 1);
          break;
        case "ArrowUp":
          e.preventDefault();
          if (!this.open) return this.show("");
          this.highlight(this.active - 1 < 0 ? n - 1 : this.active - 1);
          break;
        case "Home":
          if (this.open) {
            e.preventDefault();
            this.highlight(0);
          }
          break;
        case "End":
          if (this.open) {
            e.preventDefault();
            this.highlight(n - 1);
          }
          break;
        case "Enter":
          if (this.open && this.rows[this.active]) {
            e.preventDefault();
            this.pick(this.rows[this.active].value);
          }
          break;
        case "Escape":
          if (this.open) {
            e.preventDefault();
            this.hide();
            this.input.value = this.labelOf(this.value);
          }
          break;
        default:
          break;
      }
    }
  }

  window.DvlGearCombo = DvlGearCombo;
})();
